/**
 * FrameCacheMemoryGovernor
 * -----------------------
 * Bounded memory budget for Phase D's scrub-frame cache. Every cached
 * ImageBitmap counts against a total-across-all-clips budget so one
 * pathological clip can't eat all available heap.
 *
 * Default budget: 256 MB total. At 1080p × 4 bytes/pixel ≈ 8.3 MB/frame,
 * that's ~30 frames total across the whole editor — a conservative floor
 * that still gives scrubbing a useful window.
 *
 * Auto-shrink: when `performance.memory.usedJSHeapSize / jsHeapSizeLimit`
 * exceeds 0.85, the budget scales down proportionally toward a 32 MB floor.
 * Called periodically from Editor2TimelineShell via Task D6's monitor.
 *
 * All methods are synchronous and cheap — this is a pure bookkeeping
 * module with no DOM or React dependencies, and unit-testable with
 * vitest in node env.
 *
 * Tracked in docs/plans/2026-04-08-studio-canva-smoothness.md (Task D1).
 */

const DEFAULT_BUDGET_BYTES = 256 * 1024 * 1024;
const MIN_BUDGET_BYTES = 32 * 1024 * 1024;
const HEAP_PRESSURE_THRESHOLD = 0.85;

export class FrameCacheMemoryGovernor {
  private totalBudget: number;
  private readonly reserved = new Map<string, number>();
  private usedBytes = 0;

  constructor(budget: number = DEFAULT_BUDGET_BYTES) {
    this.totalBudget = budget;
  }

  /**
   * Current total budget across all clips.
   */
  getTotalBudgetBytes(): number {
    return this.totalBudget;
  }

  /**
   * Try to reserve `bytes` for `elementId`. If the new total would exceed
   * the budget, returns false WITHOUT modifying state. On success, updates
   * the reservation for that element (replacing any previous value).
   */
  tryReserve(elementId: string, bytes: number): boolean {
    const existing = this.reserved.get(elementId) ?? 0;
    const delta = bytes - existing;
    if (this.usedBytes + delta > this.totalBudget) return false;
    this.reserved.set(elementId, bytes);
    this.usedBytes += delta;
    return true;
  }

  /**
   * Release all bytes reserved for `elementId`. No-op if the element
   * was never reserved.
   */
  release(elementId: string): void {
    const existing = this.reserved.get(elementId);
    if (existing === undefined) return;
    this.reserved.delete(elementId);
    this.usedBytes -= existing;
  }

  /**
   * Compute a per-clip frame capacity given the current budget, the
   * number of clips competing for memory, and the per-frame byte cost.
   *
   * Returns 0 if `clipCount` or `bytesPerFrame` is non-positive.
   */
  computePerClipCapacity(args: { bytesPerFrame: number; clipCount: number }): number {
    if (args.clipCount <= 0 || args.bytesPerFrame <= 0) return 0;
    const perClipBytes = this.totalBudget / args.clipCount;
    return Math.max(0, Math.floor(perClipBytes / args.bytesPerFrame));
  }

  /**
   * Re-evaluate the budget based on current heap pressure. Called
   * periodically from the D6 monitor. When heap usage exceeds 85% of
   * jsHeapSizeLimit, scales the budget down linearly toward MIN_BUDGET_BYTES.
   * When heap is healthy, restores the default budget.
   *
   * No-op on browsers without `performance.memory` (everything except
   * Chromium). In that case the budget stays at its construction-time
   * default — no auto-shrink, manual release only.
   */
  reevaluate(): void {
    const mem = (performance as any).memory;
    if (
      !mem ||
      typeof mem.usedJSHeapSize !== "number" ||
      typeof mem.jsHeapSizeLimit !== "number" ||
      mem.jsHeapSizeLimit <= 0
    ) {
      return;
    }
    const ratio = mem.usedJSHeapSize / mem.jsHeapSizeLimit;
    if (ratio > HEAP_PRESSURE_THRESHOLD) {
      // Scale budget inversely to pressure, floored at MIN_BUDGET_BYTES.
      // pressureFactor ranges from 1.0 (just at threshold) down to 0.0
      // (at the heap limit). Budget = default * factor, clamped to the floor.
      const pressureFactor = Math.max(
        0,
        1 - (ratio - HEAP_PRESSURE_THRESHOLD) / (1 - HEAP_PRESSURE_THRESHOLD)
      );
      this.totalBudget = Math.max(
        MIN_BUDGET_BYTES,
        Math.floor(DEFAULT_BUDGET_BYTES * pressureFactor)
      );
    } else {
      this.totalBudget = DEFAULT_BUDGET_BYTES;
    }
  }

  /**
   * Read-only snapshot of the governor's internal counters. Used by
   * the Perf HUD (Task D5).
   */
  getStats(): {
    totalBudgetBytes: number;
    usedBytes: number;
    reservedElements: number;
  } {
    return {
      totalBudgetBytes: this.totalBudget,
      usedBytes: this.usedBytes,
      reservedElements: this.reserved.size,
    };
  }
}

/**
 * Exported constants for tests and for cross-referencing from callers
 * that want to know the default budget without constructing a governor.
 */
export const FRAME_CACHE_DEFAULT_BUDGET_BYTES = DEFAULT_BUDGET_BYTES;
export const FRAME_CACHE_MIN_BUDGET_BYTES = MIN_BUDGET_BYTES;
export const FRAME_CACHE_HEAP_PRESSURE_THRESHOLD = HEAP_PRESSURE_THRESHOLD;
